export type AssessmentNumberBounds = {
  min?: number;
  max?: number;
};

export type AssessmentNumberFailReason = 'not-a-number' | 'before-min' | 'after-max';

/** Outcome of validating an assessment numeric-free-text answer. */
export type AssessmentNumberValidation = {
  /** True when `normalizedValue` is a real number on or after `min` (and on or before `max` when set). */
  isValid: boolean;
  /**
   * Inclusive lower bound, when authored. Absent when the question carries no
   * floor — unlike a date question, a numeric question has no default floor:
   * "today's defaults" for a question with no authored bounds is no bound at
   * all, only the shape check below.
   */
  min?: number;
  /** Inclusive upper bound, when authored. Absent when there is no ceiling. */
  max?: number;
  /**
   * The answer parsed to a number, or `null` when the input isn't a plain
   * non-negative integer or decimal (letters, a sign, more than one decimal
   * point, or blank).
   */
  normalizedValue: number | null;
  /** Set when `isValid` is false so the screen can pick error copy without re-comparing bounds. */
  reason?: AssessmentNumberFailReason;
};

// Non-negative integer or decimal only — matches every numeric field this
// validates today (goal weight, highest adult weight, ZIP). No sign, no
// thousands separators, no exponent notation: those would all need explicit
// product sign-off before a member could type them into a lbs/ZIP field.
const NUMERIC_PATTERN = /^\d+(\.\d+)?$/;

/**
 * Validates a numeric-free-text answer. Mirrors `validateAssessmentDate`'s
 * shape (isValid / bounds / normalized value / reason) so a screen can pick
 * error copy the same way for both, but carries no default bound of its own:
 * a numeric question with no authored `min`/`max` only gets the shape check
 * (reject letters/symbols), matching "today's defaults" for a question that
 * never had validation before this.
 */
export function validateAssessmentNumber(
  input?: string | null,
  bounds?: AssessmentNumberBounds
): AssessmentNumberValidation {
  const { min, max } = bounds ?? {};

  const trimmed = (input ?? '').trim();
  const parsed = NUMERIC_PATTERN.test(trimmed) ? Number(trimmed) : NaN;
  const normalizedValue = Number.isFinite(parsed) ? parsed : null;

  const isValid =
    normalizedValue !== null &&
    (min === undefined || normalizedValue >= min) &&
    (max === undefined || normalizedValue <= max);

  const reason: AssessmentNumberFailReason | undefined = isValid
    ? undefined
    : normalizedValue === null
      ? 'not-a-number'
      : max !== undefined && normalizedValue > max
        ? 'after-max'
        : 'before-min';

  return {
    isValid,
    normalizedValue,
    ...(min !== undefined ? { min } : {}),
    ...(max !== undefined ? { max } : {}),
    ...(reason ? { reason } : {}),
  };
}
